import { Box, Text } from '@chakra-ui/react'
import { OfferCar } from './OfferCar'
import { useContext, useEffect, useState } from 'react'
import { Loading } from '../../components/Loading'
import { VariantGroupingData, RiskVariantInfo } from './OffersCar'
import { InsuranceParamsDto, Offer, OfferAddon, OfferVehicle, Variant } from 'BrokerTsLib/src/dtos'
import React from 'react'
import { OfferExtraCar } from './OfferExtraCar'
import { GroupedOffers, GroupedOffersProps } from './GroupedOffers'
import { Risk } from 'BrokerTsLib/src/enums'
import { carkulkaOffersContext } from '../../context/CarkulkaOffersProvider'
import { useGetRiskVariants } from '../../hooks'
import { useApp } from '../../hooks/useApp'
import { carkulkaExtraOffersContext } from '../../context/CarkulkaExtraOffersProvider'
import { SortBy } from '../../context/OffersProvider'
import { useInsuranceParamsContext } from '../../layouts/Wizard'

type VariantDescriptionParts = {
  pov: string | null
  hav: string | null
  spol: string | null
  glass: string | null
}

const ListTitle = ({ children }: { children: React.ReactNode }) => {
  return <Text sx={{ fontSize: 'sm', fontWeight: '600' }}>{children}</Text>
}

export const sortByValidFirst = <T extends Offer>(offers: T[], sortFc: (a: T, b: T) => number) => {
  return offers.sort((a, b) => {
    var validDiff = a.errors.length - b.errors.length

    if (validDiff) return validDiff

    return sortFc(a, b)
  })
}

const sortByVariantFirst = <T extends Offer>(
  offers: T[],
  sortFc: (a: T, b: T) => number,
  variantGroupingData: VariantGroupingData,
  insuranceVariants: Variant[]
) => {
  return offers.sort((a, b) => {
    var descA = getSingleStringVariantDescription(getVariantDesc(a, insuranceVariants, variantGroupingData))
    var descB = getSingleStringVariantDescription(getVariantDesc(b, insuranceVariants, variantGroupingData))

    var cmp = descA.localeCompare(descB)

    if (cmp !== 0) {
      return cmp
    }

    var validDiff = a.errors.length - b.errors.length

    if (validDiff) return validDiff

    return sortFc(a, b)
  })
}

const getPartDescription = (riskId: number, offer: Offer, insuranceVariants: Variant[], riskVariants: RiskVariantInfo[]) => {
  const riskVariantId = insuranceVariants
    .find(x => x.id == offer.insuranceVariantId)
    ?.variantParts
    ?.find(x => x.risk == riskId)
    ?.riskVariant

  const part = offer.parts.find((p) => p.riskId == riskId && !!riskVariantId && p.included)
  if (part) {
    const riskVariant = riskVariants.find((p) => p.id == riskVariantId)
    if (riskVariant) {
      return riskVariant.name
    }
  }
  return null
}

const getVariantParameters = (partsDesc: VariantDescriptionParts) => {
  let parameters = []

  if (partsDesc.pov) {
    parameters.push({
      label: 'Povinné ručení',
      value: partsDesc.pov,
    })
  }
  if (partsDesc.hav) {
    parameters.push({
      label: 'Havarijní pojištění',
      value: partsDesc.hav + (!!partsDesc.spol ? ' ' + partsDesc.spol : ''),
    })
  }

  if (partsDesc.glass) {
    parameters.push({
      label: 'Skla',
      value: partsDesc.glass,
    })
  }

  return parameters
}

const getSingleStringVariantDescription = (partsDesc: VariantDescriptionParts) => {
  let desc = ''
  if (partsDesc.pov) {
    desc = desc + partsDesc.pov
  }
  if (partsDesc.hav) {
    desc = desc + partsDesc.hav
  }
  if (partsDesc.spol) {
    desc = desc + partsDesc.spol
  }
  if (partsDesc.glass) {
    desc = desc + partsDesc.glass
  }
  return desc
}

const getVariantDesc = (offer: Offer, insuranceVariants: Variant[], variantGroupingData: VariantGroupingData) => {
  return {
    pov: getPartDescription(Risk.POV, offer, insuranceVariants, variantGroupingData.pov),
    hav: getPartDescription(Risk.HAV, offer, insuranceVariants, variantGroupingData.hav),
    spol: getPartDescription(Risk.SPOL, offer, insuranceVariants, variantGroupingData.spol),
    glass: getPartDescription(Risk.SKLA, offer, insuranceVariants, variantGroupingData.glass),
  }
}

export const groupOffers = (
  offers: OfferVehicle[],
  showAll: boolean,
  variantGroupingData: VariantGroupingData,
  insuranceVariants: Variant[]
) => {
  let lastDescription = ''
  let group: GroupedOffersProps | null = null
  let groups: GroupedOffersProps[] = []
  let index = 0

  offers
    .filter((x) => showAll || x.included)
    .forEach((offer) => {
      var descriptionParts = getVariantDesc(offer, insuranceVariants, variantGroupingData)
      var simpleDescription = getSingleStringVariantDescription(descriptionParts)
      if (lastDescription != simpleDescription) {
        lastDescription = simpleDescription
        group = {
          parameters: getVariantParameters(descriptionParts),
          offerId: (index++).toString(),
          offers: [offer],
        }
        groups.push(group)
      } else {
        group?.offers.push(offer)
      }
    })

  return groups
}

export const OffersCarList = () => {
  const { sortBy, offers, isLoading, showAll } = useContext(carkulkaOffersContext)
  const { offers: addonOffers } = useContext(carkulkaExtraOffersContext)
  const { insuranceParams: ip } = useInsuranceParamsContext()
  const insuranceParams = ip as InsuranceParamsDto

  const { app } = useApp()
  const onlyAddons = app === 'carkulka-extra'

  const [povVariantData, setPovVariantData] = useState<RiskVariantInfo[]>([])
  const [havVariantData, setHavVariantData] = useState<RiskVariantInfo[]>([])
  const [spolVariantData, setSpolVariantData] = useState<RiskVariantInfo[]>([])
  const [sklaVariantData, setSklaVariantData] = useState<RiskVariantInfo[]>([])

  const { refetch: loadPovRiskVariants } = useGetRiskVariants(Risk.POV, {
    enabled: false,
    onSuccess: (({ data }) => {
      setPovVariantData(data.map<RiskVariantInfo>((rv) => ({ id: rv.id, name: rv.name })))
    })
  })
  const { refetch: loadHavRiskVariants } = useGetRiskVariants(Risk.HAV, {
    enabled: false,
    onSuccess: (({ data }) => {
      setHavVariantData(data.map<RiskVariantInfo>((rv) => ({ id: rv.id, name: rv.name })))
    })
  })
  const { refetch: loadSpolRiskVariants } = useGetRiskVariants(Risk.SPOL, {
    enabled: false,
    onSuccess: (({ data }) => {
      setSpolVariantData(data.map<RiskVariantInfo>((rv) => ({ id: rv.id, name: rv.name })))
    })
  })
  const { refetch: loadGlassRiskVariants } = useGetRiskVariants(Risk.SKLA, {
    enabled: false,
    onSuccess: (({ data }) => {
      setSklaVariantData(data.map<RiskVariantInfo>((rv) => ({ id: rv.id, name: rv.name })))
    })
  })

  let groupedOffers: GroupedOffersProps[] = []

  const variantGroupingData = {
    pov: povVariantData,
    hav: havVariantData,
    spol: spolVariantData,
    glass: sklaVariantData,
  }

  // ziskanni / zajisteni vytvoreni offers
  useEffect(() => {
    if (!onlyAddons) {
      loadPovRiskVariants()
      loadHavRiskVariants()
      loadSpolRiskVariants()
      loadGlassRiskVariants()
    }
  }, []);

  // serazeni aktualnich kalkulaci
  var sorted = offers
  switch (sortBy) {
    case SortBy.Price:
      sorted = sortByValidFirst(offers, (a, b) =>
        !!a.price && (!b.price || a.price < b.price) ? -1 : 1,
      )
      break

    case SortBy.Insurer:
      sorted = sortByValidFirst(offers, (a, b) => a.insurerName.localeCompare(b.insurerName))
      break

    case SortBy.VariantPrice:
      sorted = sortByVariantFirst(
        offers,
        (a, b) => (!!a.price && (!b.price || a.price < b.price) ? -1 : 1),
        variantGroupingData!,
        insuranceParams.baseParams.variants ?? []
      )
      groupedOffers = groupOffers(sorted, showAll, variantGroupingData!, insuranceParams.baseParams.variants ?? [])
      break

    case SortBy.VariantInsurer:
      sorted = sortByVariantFirst(
        offers,
        (a, b) => a.insurerName.localeCompare(b.insurerName),
        variantGroupingData!,
        insuranceParams.baseParams.variants ?? []
      )
      groupedOffers = groupOffers(sorted, showAll, variantGroupingData!, insuranceParams.baseParams.variants ?? [])
      break

    default:
      break
  }

  const filtered = sorted.filter((x) => showAll || x.included)

  const extraOffers = addonOffers.reduce(
    (p: { [n: number]: OfferAddon[] }, c) => ({
      ...p,
      [c.riskVariantId]: [...(p[c.riskVariantId] || []), c],
    }),
    {},
  )

  return (
    <Box sx={{ display: 'grid', gap: 12 }} id="offersWrapper">
      {isLoading && <Loading large={true} />}

      {extraOffers && Object.keys(extraOffers).length > 0 && (
        <Box sx={{ display: 'grid', gap: 4 }}>
          {!onlyAddons &&
            <ListTitle>Nabídky doplňkových pojistitelů</ListTitle>
          }

          <Box sx={{ display: 'grid', gap: 12 }}>
            {Object.keys(extraOffers).map((key) => (
              <OfferExtraCar key={key} offers={extraOffers[parseInt(key)]} />
            ))}
          </Box>
        </Box>
      )}

      {!onlyAddons && filtered && (
        <Box sx={{ display: 'grid', gap: 4 }}>
          {extraOffers && Object.keys(extraOffers).length > 0 &&
            <ListTitle>Nabídky hlavních pojistitelů</ListTitle>
          }

          {groupedOffers && groupedOffers.length == 0 && (
            <Box sx={{ display: 'grid', gap: 12 }}>
              {filtered.map((offer) => {
                return <OfferCar key={offer.offerId} offer={offer} />
              })}
            </Box>
          )}

          {groupedOffers &&
            groupedOffers.length > 0 &&
            groupedOffers.map((groupOff) => {
              return (
                <GroupedOffers
                  parameters={groupOff.parameters}
                  offerId={groupOff.offerId}
                  offers={groupOff.offers}
                  key={groupOff.offerId}
                />
              )
            })}
        </Box>
      )}
    </Box>
  )
}
